import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Stack, Button } from "@mui/material";
import { format, startOfWeek, addWeeks } from "date-fns";

import useProject from "../../hooks/useProject";
import usePage from "../../hooks/usePage";
import useBrowser from "../../hooks/useBrowser";

import Page from "./Page";
import Card from "../layout/Card";
import FieldValue from "../elements/FieldValue";
import Loading from "../ui/Loading";
import EditorToolbar from "../editors/EditorToolbar";
import WeeklyCalls from "../ui/WeeklyCalls";
import Dialog_YesNo from "../ui/Dialog_YesNo";
import DateField from "../elements/Date";

const ProjectSchedule = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showMessage } = usePage();
  const { windowHeight } = useBrowser();

  const {
    getProject,
    project,
    getProjectInfo,
    projectInfo,
    scheduleBasedOnFrequency,
    removeCalls,
  } = useProject();

  const [refreshData, setRefreshData] = useState(0);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [startOn, setStartOn] = useState("");

  const projectId = location.state.projectId;
  const clientId = location.state.clientId;

  const email = useSelector((state) => state.authentication.email);

  useEffect(() => {
    getData();
  }, [projectId]);

  const getData = async () => {
    if (!projectId) return;

    await getProject(projectId);
    setRefreshData((current) => current + 1);
  };

  useEffect(() => {
    if (!project) return;

    getProjectInfo();
  }, [project]);

  useEffect(() => {
    if (!projectInfo) return;

    let frequencyStart = "";

    if (projectInfo.lastScheduleDate) {
      frequencyStart = startOfWeek(addWeeks(projectInfo.lastScheduleDate, 1));
    } else if (projectInfo.startsOn) {
      frequencyStart = startOfWeek(projectInfo.startsOn);
    }

    setStartOn(frequencyStart);
  }, [projectInfo]);

  const handleToolbarClick = async (action) => {
    switch (action) {
      case "back":
        navigate("/project", { state: { clientId, projectId } });
        break;

      case "refresh":
        await getData();
        break;

      case "delete":
        setOpenDeleteDialog(true);
        break;

      case "bulkEdit":
        navigate("/bulkcalleditor", { state: { projectId } });
        break;
    }
  };

  const handleDelete_Yes = async () => {
    setOpenDeleteDialog(false);
    await removeCalls(projectId);
    await getData();
  };

  const handleDelete_No = () => {
    setOpenDeleteDialog(false);
  };

  const handleScheduleBasedOnFrequency = async () => {
    await scheduleBasedOnFrequency(startOn);
    showMessage("You will receive an email when the scheduling is complete.");
  };

  return (
    <>
      <Page
        entityName={project?.name}
        pageName="Project Schedule"
        includeToolbar={true}
        items={[
          {
            name: "back",
            iconName: "ArrowCircleLeft",
          },
          {
            name: "refresh",
            iconName: "Refresh",
          },
          {
            name: "bulkEdit",
            iconName: "Edit",
          },
          {
            name: "delete",
            iconName: "Delete",
          },
        ]}
        onToolItemClick={handleToolbarClick}
      >
        <Box
          sx={{ display: "flex" }}
          gap={1}
        >
          <Card
            title="Information"
            sx={{
              flex: 1,
              maxHeight: `${windowHeight - 150}px`,
              overflow: "auto",
            }}
          >
            <Box style={styles.constainer}>
              {projectInfo ? (
                <Stack>
                  <FieldValue
                    label="Is Active"
                    value={projectInfo.isActive ? "Yes" : "No"}
                  />
                  <FieldValue
                    label="Is Syndicated"
                    value={projectInfo.isSyndicated ? "Yes" : "No"}
                  />
                  <FieldValue
                    label="Starts On"
                    value={
                      projectInfo.startsOn
                        ? format(projectInfo.startsOn, "MMM dd, yyy")
                        : ""
                    }
                  />
                  <FieldValue
                    label="Ends On"
                    value={
                      projectInfo.endsOn
                        ? format(projectInfo.endsOn, "MMM dd, yyy")
                        : ""
                    }
                  />
                  <FieldValue
                    label="Reps can change"
                    value={projectInfo.repsCanChange ? "Yes" : "No"}
                  />
                  <FieldValue
                    label="Reps can add"
                    value={projectInfo.repsCanAdd ? "Yes" : "No"}
                  />
                  <FieldValue
                    label="Call Duration"
                    value={projectInfo.callDuration}
                  />
                  <FieldValue
                    label="Locations"
                    value={projectInfo.locations}
                  />
                  <FieldValue
                    label="Calls"
                    value={projectInfo.callsScheduled}
                  />
                  <FieldValue
                    label="Has Frequency"
                    value={projectInfo.hasFrequency ? "Yes" : "No"}
                  />
                  <FieldValue
                    label="Last Schedule Date"
                    value={
                      projectInfo.lastScheduleDate
                        ? format(projectInfo.lastScheduleDate, "MMM dd, yyyy")
                        : "No schedule"
                    }
                  />

                  {projectInfo.hasFrequency && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "8px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                        }}
                      >
                        <DateField
                          label="Next Cycle Starts"
                          value={startOn ? startOn : ""}
                          onChange={(newValue) => setStartOn(newValue)}
                        />
                        <Button
                          variant="contained"
                          onClick={handleScheduleBasedOnFrequency}
                        >
                          Schedule
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Stack>
              ) : (
                <Loading />
              )}
            </Box>
          </Card>

          <Card
            title="Scheduled Calls"
            sx={{
              flex: 3,
              height: `${windowHeight - 150}px`,
              overflow: "auto",
            }}
          >
            <Box>
              <Box
                sx={{
                  height: `${windowHeight - 210}px`,
                  overflow: "auto",
                  padding: "8px",
                }}
              >
                <WeeklyCalls
                  projectId={projectId}
                  onRefresh={getData}
                  refreshData={refreshData}
                />
              </Box>
            </Box>
          </Card>
        </Box>
      </Page>

      {openDeleteDialog && (
        <Dialog_YesNo
          title="Delete all calls?"
          message="All calls, not completed, will be deleted. Are you sure you want to proceed?"
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          onYes={handleDelete_Yes}
          onNo={handleDelete_No}
        />
      )}
    </>
  );
};

const styles = {
  constainer: {
    padding: "8px",
  },
};

export default ProjectSchedule;
